import {
  type Configuration,
  LogLevel,
  PublicClientApplication,
  type RedirectRequest,
} from "@azure/msal-browser";

export interface AuthSettings {
  tenantId: string;
  clientId: string;
  apiScope: string;
  apiBaseUrl: string;
}

export class MissingConfigurationError extends Error {
  constructor(missing: string[]) {
    super(
      `Missing required configuration: ${missing.join(", ")}. Copy .env.example to .env.local and restart the dev server.`,
    );
    this.name = "MissingConfigurationError";
  }
}

export function readAuthSettings(env: ImportMetaEnv = import.meta.env): AuthSettings {
  const values = {
    VITE_ENTRA_TENANT_ID: env.VITE_ENTRA_TENANT_ID,
    VITE_ENTRA_CLIENT_ID: env.VITE_ENTRA_CLIENT_ID,
    VITE_API_SCOPE: env.VITE_API_SCOPE,
  };

  const missing = Object.entries(values)
    .filter(([, value]) => !value)
    .map(([key]) => key);

  if (missing.length > 0) {
    throw new MissingConfigurationError(missing);
  }

  return {
    tenantId: values.VITE_ENTRA_TENANT_ID!,
    clientId: values.VITE_ENTRA_CLIENT_ID!,
    apiScope: values.VITE_API_SCOPE!,
    apiBaseUrl: (env.VITE_API_BASE_URL ?? "").replace(/\/$/, ""),
  };
}

export function buildMsalConfig(settings: AuthSettings): Configuration {
  return {
    auth: {
      clientId: settings.clientId,
      authority: `https://login.microsoftonline.com/${settings.tenantId}`,
      redirectUri: window.location.origin,
      postLogoutRedirectUri: window.location.origin,
      navigateToLoginRequestUrl: true,
    },
    cache: {
      // Local rather than session storage, so opening the app again (from the home screen, or a
      // new tab) does not send everyone back through Microsoft sign-in each time.
      cacheLocation: "localStorage",
      storeAuthStateInCookie: false,
    },
    system: {
      loggerOptions: {
        logLevel: import.meta.env.DEV ? LogLevel.Warning : LogLevel.Error,
        piiLoggingEnabled: false,
        loggerCallback: (level, message, containsPii) => {
          if (containsPii) return;
          if (level === LogLevel.Error) console.error(message);
          else if (level === LogLevel.Warning) console.warn(message);
        },
      },
    },
  };
}

export function buildLoginRequest(settings: AuthSettings): RedirectRequest {
  return { scopes: [settings.apiScope] };
}

/**
 * Where Microsoft sends a quiet token renewal: a blank page, so the hidden frame loads it at once
 * rather than starting the whole app inside it. It's the one page the site lets itself frame.
 */
export const silentRedirectUri = (): string => `${window.location.origin}/redirect.html`;

export function createMsalInstance(settings: AuthSettings): PublicClientApplication {
  return new PublicClientApplication(buildMsalConfig(settings));
}
